import React from 'react';
import { ComponentSkinProps } from '../../../../types/component-skin';
import type { BannerItem } from '../MainBanner.types';
import { useTranslation } from '../../../../redux/TranslationHelper';
import './BasicMainBannerSkin.css';

/**
 * MainBanner 컴포넌트의 기본 스킨
 * 슬라이더, 리스트, 단일 배너 모드를 모두 지원합니다.
 */
const BasicMainBannerSkin: React.FC<ComponentSkinProps> = ({
  data = {},
  actions = {},
  options = {},
  mode,
  utils = {},
  app = {},
  editor = {}
}) => {
  // 다국어 처리
  const { t } = useTranslation();
  
  // 데이터 추출 - 타입 안전하게 처리
  const skinData = data as any; // 임시로 any 타입 사용
  
  const {
    currentIndex = 0,
    isTransitioning = false,
    isPaused = false,
    isLoading = false,
    isVideoPlaying = false,
    isHovering = false,
    banners = [],
    bannerMode = 'slider',
    autoPlay = true,
    autoPlaySpeed = 5000,
    transitionSpeed = 500,
    showDots = true,
    showArrows = true,
    pauseOnHover = true,
    infiniteLoop = true,
    containerHeight = '600px',
    isMobile = false,
    isEditorMode = false,
    isPreviewMode = false,
    getImageSource = (banner: BannerItem, index: number) => {
      return banner.icon || 'https://via.placeholder.com/1200x400';
    },
    videoRef
  } = skinData;
  
  // 액션 추출
  const {
    goToNext = () => {},
    goToPrev = () => {},
    goToSlide = () => {},
    onMouseEnter = () => {},
    onMouseLeave = () => {},
    onTouchStart = () => {},
    onTouchMove = () => {},
    onTouchEnd = () => {},
    onBannerClick = () => {},
    onVideoClick = () => {},
    onVideoEnded = () => {},
    onVideoPlaying = () => {},
    onVideoError = () => {},
    setVideoRef = () => {}
  } = actions;
  
  // 에디터 속성
  const { 
    isSelected = false, 
    onSelect = () => {}, 
    dragHandleProps = {} 
  } = editor;

  // 컴포넌트 props (스타일 등) - 여러 경로에서 className 시도
  const propsClassName = skinData.props?.className || '';
  const directClassName = skinData.className || '';
  const componentPropsClassName = skinData.componentProps?.className || '';
  
  // 모든 가능한 className 소스를 합침
  const className = [propsClassName, directClassName, componentPropsClassName]
    .filter(Boolean)
    .join(' ');
  
  const style = skinData.props?.style || skinData.style || {};
  

  // 스타일 문자열을 객체로 변환하는 함수
  const parseStyleString = (styleStr: string | undefined): React.CSSProperties => {
    if (!styleStr) return {};
    
    const styleObj: Record<string, string> = {};
    const styles = styleStr.split(';');
    
    styles.forEach(style => {
      const [property, value] = style.split(':').map(item => item?.trim());
      if (property && value) {
        // 카멜 케이스로 변환 (예: background-color -> backgroundColor)
        const camelProperty = property.replace(/-([a-z])/g, (g) => g[1].toUpperCase());
        styleObj[camelProperty] = value;
      }
    });
    
    return styleObj;
  };

  // 로딩 중 표시
  if (isLoading) {
    return <div className="mainbanner-loading">{t('배너 로딩 중...')}</div>;
  }

  // 배너가 없는 경우 빈 배너 표시
  if (!banners || banners.length === 0) {
    return (
      <div className="mainbanner-container" style={style}>
        <div className="mainbanner-slide-content" style={{ backgroundColor: '#f5f5f5' }} />
        <div className="mainbanner-content position-5" style={{ color: '#333333', textShadow: 'none' }}>
          <h2 className="mainbanner-title">{t('배너가 없습니다')}</h2>
          <p className="mainbanner-description">{t('배너를 추가해주세요')}</p>
        </div>
      </div>
    );
  }

  // 배너가 하나뿐인 경우 단일 배너 표시
  if (banners.length === 1) {
    const banner = banners[0];
    const customStyle = typeof banner.style === 'string' ? parseStyleString(banner.style) : (banner.style || {});
    const isVideo = banner.mediaType === 'video' && banner.videoUrl;
    
    // 이미지 소스 가져오기
    const imgSrc = getImageSource(banner, 0);
    
    return (
      <div 
        className={`mainbanner-container ${isMobile ? 'mobile-full-height' : ''} ${className}`} 
        style={{ ...style, ...customStyle }}
        {...(isEditorMode ? dragHandleProps : {})}
        onClick={isEditorMode ? onSelect : undefined}
      >
        {isVideo ? (
          <div 
            className="mainbanner-video-container" 
            onClick={onVideoClick} 
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
            style={{ width: '100%', height: '100%', cursor: 'pointer', position: 'relative' }}
          >
            <video
              ref={setVideoRef}
              className="mainbanner-video"
              src={banner.videoUrl}
              autoPlay={banner.autoplay}
              playsInline
              muted={banner.muted}
              loop={banner.loop}
              preload="auto"
              disablePictureInPicture
              onError={onVideoError}
              onPlay={onVideoPlaying}
              onEnded={() => !banner.loop && onVideoEnded()}
            />
            <div 
              className="mainbanner-video-play-button" 
              onClick={(e) => {
                e.stopPropagation();
                onVideoClick();
              }}
              style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                color: 'white',
                borderRadius: '50%',
                width: '60px',
                height: '60px',
                display: isHovering || !isVideoPlaying ? 'flex' : 'none',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: '0.8',
                transition: 'opacity 0.2s, display 0.2s',
                zIndex: 10
              }}
              onMouseOver={(e) => {
                (e.currentTarget as HTMLDivElement).style.opacity = '1';
              }}
              onMouseOut={(e) => {
                (e.currentTarget as HTMLDivElement).style.opacity = '0.8';
              }}
            >
              {isVideoPlaying ? (
                // 정지 아이콘
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <rect x="6" y="4" width="4" height="16" fill="white" />
                  <rect x="14" y="4" width="4" height="16" fill="white" />
                </svg>
              ) : (
                // 재생 아이콘
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8 5V19L19 12L8 5Z" fill="white" />
                </svg>
              )}
            </div>
          </div>
        ) : (
          isMobile ? (
            <div 
              className="mainbanner-slide-content" 
              style={{ backgroundImage: `url(${imgSrc})` }}
            />
          ) : (
            <img 
              className="mainbanner-image" 
              src={imgSrc} 
              alt={banner.text || t('배너 이미지')} 
            />
          )
        )}
        
        {(banner.showTitle || banner.showButton) && (
          <div 
            className={`mainbanner-content position-${banner.position} ${banner.mobileFullWidth ? 'mobile-full-width' : ''} ${banner.hasBackground ? 'has-background' : ''}`}
            style={{ 
              color: banner.textColor || 'white',
              textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)'
            }}
          >
            {banner.showTitle && banner.description && (
              <p className="mainbanner-description">{t(banner.description)}</p>
            )}
            {banner.showTitle && banner.text && (
              <h2 className="mainbanner-title">{t(banner.text)}</h2>
            )}
            
            {banner.showButton && banner.url && banner.url !== '#' && (
              <button 
                className={`mainbanner-cta-button ${banner.transparentButton ? 'transparent' : ''}`}
                onClick={() => onBannerClick(banner)}
                style={{
                  backgroundColor: banner.transparentButton ? 'transparent' : (banner.buttonBgColor || '#ff6b6b'),
                  color: banner.buttonTextColor || 'white',
                  borderColor: banner.buttonTextColor || 'white',
                  width: isMobile ? (banner.mobileButtonWidth || banner.buttonWidth || 'auto') : (banner.buttonWidth || 'auto'),
                  height: isMobile ? (banner.mobileButtonHeight || banner.buttonHeight || 'auto') : (banner.buttonHeight || 'auto')
                }}
              >
                {t(banner.buttonText || '자세히 보기')}
              </button>
            )}
          </div>
        )}
      </div>
    );
  }

  // 리스트 모드 (가로 스크롤)
  if (bannerMode === 'list') {
    return (
      <div 
        className={`mainbanner-list-container ${className}`} 
        style={style}
        {...(isEditorMode ? dragHandleProps : {})}
        onClick={isEditorMode ? onSelect : undefined}
      >
        {banners.map((banner: BannerItem, index: number) => {
          const customStyle = typeof banner.style === 'string' ? parseStyleString(banner.style) : (banner.style || {});
          const imgSrc = getImageSource(banner, index);
          const isVideo = banner.mediaType === 'video' && banner.videoUrl;
          
          return (
            <div key={index} className="mainbanner-list-item" style={customStyle}>
              {isVideo ? (
                <div 
                  className="mainbanner-video-container" 
                  data-index={index}
                  onClick={() => {
                    const video = document.querySelector(`.mainbanner-list-item:nth-child(${index + 1}) video`) as HTMLVideoElement;
                    if (video) {
                      if (video.paused) {
                        video.play().catch(error => console.error(t('비디오 재생 중 오류 발생:'), error));
                      } else {
                        video.pause();
                      }
                    }
                  }} 
                  onMouseEnter={(e) => {
                    const container = e.currentTarget;
                    const button = container.querySelector('.mainbanner-video-play-button') as HTMLElement;
                    if (button) button.style.display = 'flex';
                  }}
                  onMouseLeave={(e) => {
                    const container = e.currentTarget;
                    const button = container.querySelector('.mainbanner-video-play-button') as HTMLElement;
                    const video = container.querySelector('video') as HTMLVideoElement;
                    if (button && video && !video.paused) button.style.display = 'none';
                  }}
                  style={{ width: '100%', height: '100%', cursor: 'pointer', position: 'relative' }}
                >
                  <video
                    className="mainbanner-video"
                    src={banner.videoUrl}
                    autoPlay={banner.autoplay}
                    playsInline
                    muted={banner.muted}
                    loop={banner.loop}
                    disablePictureInPicture
                  />
                  <div 
                    className="mainbanner-video-play-button" 
                    onClick={(e) => {
                      e.stopPropagation();
                      const video = document.querySelector(`.mainbanner-list-item:nth-child(${index + 1}) video`) as HTMLVideoElement;
                      if (video) {
                        if (video.paused) {
                          video.play().catch(error => console.error(t('비디오 재생 중 오류 발생:'), error));
                        } else {
                          video.pause();
                        }
                      }
                    }}
                    style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      color: 'white',
                      borderRadius: '50%',
                      width: '60px',
                      height: '60px',
                      display: 'none',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      opacity: '0.8',
                      transition: 'opacity 0.2s',
                      zIndex: 10
                    }}
                    onMouseOver={(e) => {
                      (e.currentTarget as HTMLDivElement).style.opacity = '1';
                    }}
                    onMouseOut={(e) => {
                      (e.currentTarget as HTMLDivElement).style.opacity = '0.8';
                    }}
                  >
                    {(() => {
                      // 비디오 요소 확인 및 상태 체크
                      const videoEl = document.querySelector(`.mainbanner-list-item:nth-child(${index + 1}) video`) as HTMLVideoElement;
                      const isPaused = !videoEl || videoEl.paused;
                      
                      return isPaused ? (
                        // 재생 아이콘
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 5V19L19 12L8 5Z" fill="white" />
                        </svg>
                      ) : (
                        // 정지 아이콘
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="6" y="4" width="4" height="16" fill="white" />
                          <rect x="14" y="4" width="4" height="16" fill="white" />
                        </svg>
                      );
                    })()}
                  </div>
                </div>
              ) : (
                isMobile ? (
                  <div 
                    className="mainbanner-slide-content" 
                    style={{ backgroundImage: `url(${imgSrc})` }}
                  />
                ) : (
                  <img 
                    className="mainbanner-image" 
                    src={imgSrc} 
                    alt={banner.text || t('배너 이미지')} 
                  />
                )
              )}
              
              {(banner.showTitle || banner.showButton) && (
                <div 
                  className={`mainbanner-content position-${banner.position} ${banner.mobileFullWidth ? 'mobile-full-width' : ''} ${banner.hasBackground ? 'has-background' : ''}`}
                  style={{ 
                    color: banner.textColor || 'white',
                    textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)'
                  }}
                >
                  {banner.showTitle && banner.description && (
                    <p className="mainbanner-description">{t(banner.description)}</p>
                  )}
                  {banner.showTitle && banner.text && (
                    <h2 className="mainbanner-title">{t(banner.text)}</h2>
                  )}
                  
                  {banner.showButton && banner.url && banner.url !== '#' && (
                    <button 
                      className={`mainbanner-cta-button ${banner.transparentButton ? 'transparent' : ''}`}
                      onClick={() => onBannerClick(banner)}
                      style={{
                        backgroundColor: banner.transparentButton ? 'transparent' : (banner.buttonBgColor || '#ff6b6b'),
                        color: banner.buttonTextColor || 'white',
                        borderColor: banner.buttonTextColor || 'white',
                        width: isMobile ? (banner.mobileButtonWidth || banner.buttonWidth || 'auto') : (banner.buttonWidth || 'auto'),
                        height: isMobile ? (banner.mobileButtonHeight || banner.buttonHeight || 'auto') : (banner.buttonHeight || 'auto')
                      }}
                    >
                      {t(banner.buttonText || '자세히 보기')}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // 표준 슬라이더 모드
  return (
    <div 
      className={`mainbanner-slider-container ${className}`} 
      style={style}
      {...(isEditorMode ? dragHandleProps : {})}
      onClick={isEditorMode ? onSelect : undefined}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      <div className="mainbanner-slider-wrapper">
        {banners.map((banner: BannerItem, index: number) => {
          const customStyle = typeof banner.style === 'string' ? parseStyleString(banner.style) : (banner.style || {});
          const imgSrc = getImageSource(banner, index);
          const isVideo = banner.mediaType === 'video' && banner.videoUrl;
          
          return (
            <div key={index} className={`mainbanner-slide ${index === currentIndex ? 'active' : ''}`} style={customStyle}>
              {isVideo ? (
                <div 
                  className="mainbanner-video-container" 
                  onClick={index === currentIndex ? onVideoClick : undefined} 
                  onMouseEnter={index === currentIndex ? onMouseEnter : undefined}
                  onMouseLeave={index === currentIndex ? onMouseLeave : undefined}
                  style={{ width: '100%', height: '100%', cursor: index === currentIndex ? 'pointer' : 'default', position: 'relative' }}
                >
                  <video
                    ref={index === currentIndex ? setVideoRef : null}
                    className="mainbanner-video"
                    src={banner.videoUrl}
                    autoPlay={index === currentIndex && banner.autoplay}
                    playsInline
                    muted={banner.muted}
                    loop={banner.loop}
                    preload="auto"
                    disablePictureInPicture
                    onError={onVideoError}
                    onPlay={onVideoPlaying}
                    onEnded={() => !banner.loop && index === currentIndex && onVideoEnded()}
                  />
                  {index === currentIndex && (
                    <div 
                      className="mainbanner-video-play-button" 
                      onClick={(e) => {
                        e.stopPropagation();
                        onVideoClick();
                      }}
                      style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(0, 0, 0, 0.5)',
                        color: 'white',
                        borderRadius: '50%',
                        width: '60px',
                        height: '60px',
                        display: isHovering || !isVideoPlaying ? 'flex' : 'none',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        opacity: '0.8',
                        transition: 'opacity 0.2s',
                        zIndex: 10
                      }}
                      onMouseOver={(e) => {
                        (e.currentTarget as HTMLDivElement).style.opacity = '1';
                      }}
                      onMouseOut={(e) => {
                        (e.currentTarget as HTMLDivElement).style.opacity = '0.8';
                      }}
                    >
                      {isVideoPlaying ? (
                        // 정지 아이콘
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="6" y="4" width="4" height="16" fill="white" />
                          <rect x="14" y="4" width="4" height="16" fill="white" />
                        </svg>
                      ) : (
                        // 재생 아이콘
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M8 5V19L19 12L8 5Z" fill="white" />
                        </svg>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                isMobile ? (
                  <div 
                    className="mainbanner-slide-content" 
                    style={{ backgroundImage: `url(${imgSrc})` }}
                  />
                ) : (
                  <img 
                    className="mainbanner-image" 
                    src={imgSrc} 
                    alt={banner.text || t('배너 이미지')} 
                  />
                )
              )}
              
              {(banner.showTitle || banner.showButton) && (
                <div 
                  className={`mainbanner-content position-${banner.position} ${banner.mobileFullWidth ? 'mobile-full-width' : ''} ${banner.hasBackground ? 'has-background' : ''}`}
                  style={{ 
                    color: banner.textColor || 'white',
                    textShadow: banner.textShadow || '0 1px 2px rgba(0, 0, 0, 0.6)'
                  }}
                >
                  {banner.showTitle && banner.description && (
                    <p className="mainbanner-description">{t(banner.description)}</p>
                  )}
                  {banner.showTitle && banner.text && (
                    <h2 className="mainbanner-title">{t(banner.text)}</h2>
                  )}
                  
                  {banner.showButton && banner.url && banner.url !== '#' && (
                    <button 
                      className={`mainbanner-cta-button ${banner.transparentButton ? 'transparent' : ''}`}
                      onClick={() => onBannerClick(banner)}
                      style={{
                        backgroundColor: banner.transparentButton ? 'transparent' : (banner.buttonBgColor || '#ff6b6b'),
                        color: banner.buttonTextColor || 'white',
                        borderColor: banner.buttonTextColor || 'white',
                        width: isMobile ? (banner.mobileButtonWidth || banner.buttonWidth || 'auto') : (banner.buttonWidth || 'auto'),
                        height: isMobile ? (banner.mobileButtonHeight || banner.buttonHeight || 'auto') : (banner.buttonHeight || 'auto')
                      }}
                    >
                      {t(banner.buttonText || '자세히 보기')}
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* 네비게이션 버튼 */}
      {showArrows && banners.length > 1 && (
        <>
          <button className="mainbanner-nav-button prev" onClick={goToPrev}>&#10094;</button>
          <button className="mainbanner-nav-button next" onClick={goToNext}>&#10095;</button>
        </>
      )}
      
      {/* 인디케이터 */}
      {showDots && banners.length > 1 && (
        <div className="mainbanner-indicators">
          {banners.map((_: any, index: number) => (
            <div 
              key={index} 
              className={`mainbanner-indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default BasicMainBannerSkin;